import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "./ui/card";
import TextToSpeech from "./TextToSpeech";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  XCircle,
  Star,
  Bot,
  Loader2,
  User,
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Progress } from "./ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const ratingDescriptions = {
  5: "Absolútne súhlasím / s riešením sa plne stotožňujem",
  4: "Súhlasím / s riešením súhlasím s miernymi výhradami",
  3: "Skôr súhlasím / s riešením súhlasím s viacerými výhradami",
  2: "Skôr nesúhlasím / riešenie obsahuje prvky, pre ktoré s ním nesúhlasím",
  1: "Nesúhlasím / situáciu by som riešil úplne inak",
  0: "Absolútne nesúhlasím / riešenie je nevhodné (po odbornej stránke, v rozpore s pravidlami, s firemnou kultúrou a pod.)",
};

// Define colors for each rating value
const ratingColors = {
  5: "text-green-500 fill-current hover:text-green-400",
  4: "text-amber-500 fill-current hover:text-amber-400",
  3: "text-yellow-500 fill-current hover:text-yellow-400",
  2: "text-orange-500 fill-current hover:text-orange-400",
  1: "text-red-500 fill-current hover:text-red-400",
  0: "text-gray-900 fill-current hover:text-gray-700",
};

const EvaluateSolutions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [situation, setSituation] = useState(null);
  const [userSolution, setUserSolution] = useState(null);
  const [solutions, setSolutions] = useState([]);
  const [currentSolutionIndex, setCurrentSolutionIndex] = useState(0);
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userEvaluations, setUserEvaluations] = useState({});
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/login");
    } else {
      setUser(storedUser);
      fetchSituationAndSolutions(id, storedUser.id);
    }
  }, [id, navigate]);

  const fetchSituationAndSolutions = async (situationId, userId) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch situation details
      const { data: situationData, error: situationError } = await supabase
        .from("situations")
        .select("*")
        .eq("id", situationId)
        .single();

      if (situationError) throw situationError;
      setSituation(situationData);

      // Get the user's solution
      const { data: userSolutionData, error: userSolutionError } =
        await supabase
          .from("solutions")
          .select("*")
          .eq("situation_id", situationId)
          .eq("user_id", userId)
          .single();

      if (userSolutionError && userSolutionError.code !== "PGRST116") {
        throw userSolutionError;
      }

      setUserSolution(userSolutionData);

      if (!userSolutionData) {
        navigate(`/situation/${situationId}`);
        return;
      }

      // Check if all group members have responded before allowing evaluation
      const { data: checkFacilities } = await supabase
        .from("user_facilities")
        .select("facility_id")
        .eq("user_id", userId)
        .eq("is_active", true);

      if (checkFacilities && checkFacilities.length > 0) {
        for (const uf of checkFacilities) {
          const { data: accessUsers } = await supabase
            .from("access_control")
            .select("user_id")
            .eq("situation_id", situationId)
            .eq("facility_id", uf.facility_id);

          if (!accessUsers || accessUsers.length === 0) continue;

          const { data: facSolutions } = await supabase
            .from("solutions")
            .select("user_id")
            .eq("situation_id", situationId)
            .in("user_id", accessUsers.map((u) => u.user_id));

          const uniqueResponded = new Set(facSolutions?.map((s) => s.user_id) || []);
          if (uniqueResponded.size < accessUsers.length) {
            navigate("/situations");
            return;
          }
        }
      }

      // Get all of the user's facilities
      const { data: userFacilities, error: userFacilitiesError } =
        await supabase
          .from("user_facilities")
          .select("facility_id")
          .eq("user_id", userId)
          .eq("is_active", true);

      if (userFacilitiesError) throw userFacilitiesError;

      if (!userFacilities || userFacilities.length === 0) {
        setError("User is not assigned to any facilities");
        return;
      }

      const facilityIds = userFacilities.map((uf) => uf.facility_id);

      // Get all users from these facilities
      const { data: facilityUsers, error: facilityUsersError } = await supabase
        .from("user_facilities")
        .select("user_id")
        .in("facility_id", facilityIds)
        .eq("is_active", true);

      if (facilityUsersError) throw facilityUsersError;

      const facilityUserIds = [
        ...new Set(facilityUsers.map((fu) => fu.user_id)),
      ];

      // Get all solutions for this situation, including:
      // 1. Solutions from users in the same facilities
      // 2. AI solutions (user_id is null)
      const { data: solutionsData, error: solutionsError } = await supabase
        .from("solutions")
        .select("*")
        .eq("situation_id", situationId)
        .or(`user_id.in.(${facilityUserIds.join(",")}),user_id.is.null`);

      if (solutionsError) throw solutionsError;

      // Get all evaluations made by current user for this situation
      const { data: evaluationsData, error: evaluationsError } = await supabase
        .from("evaluations")
        .select("*")
        .eq("evaluator_id", userId)
        .in(
          "solution_id",
          solutionsData.map((s) => s.id)
        );

      if (evaluationsError) throw evaluationsError;

      const evaluations = evaluationsData.reduce((acc, evalue) => {
        acc[evalue.solution_id] = evalue.score;
        return acc;
      }, {});

      setUserEvaluations(evaluations);

      // Process solutions
      const processedSolutions = solutionsData.map((solution) => ({
        ...solution,
        isAI: solution.user_id === null,
        isUserSolution: solution.user_id === userId,
      }));

      // Sort solutions (user's last, randomize others)
      const sortedSolutions = processedSolutions.sort((a, b) => {
        if (a.isUserSolution) return 1;
        if (b.isUserSolution) return -1;
        return Math.random() - 0.5;
      });

      setSolutions(sortedSolutions);

      // Check if all solutions are already evaluated
      const allEvaluated = sortedSolutions.every(
        (solution) => evaluations[solution.id] !== undefined
      );

      if (allEvaluated) {
        setShowSummary(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (value) => {
    setRating(value);
  };

  const renderRatingStars = () => {
    return (
      <div className="flex flex-col items-center space-y-4">
        {/* Stars row */}
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => handleRatingChange(0)}
            className="focus:outline-none relative group"
          >
            <XCircle
              className={`w-8 h-8 ${
                rating === 0 ? ratingColors[0] : "text-gray-300"
              } transition-colors duration-200 ease-in-out group-hover:text-gray-700`}
            />
          </button>
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              onClick={() => handleRatingChange(value)}
              className="focus:outline-none relative group"
            >
              <Star
                className={`w-8 h-8 ${
                  value <= rating ? ratingColors[value] : "text-gray-300"
                } transition-colors duration-200 ease-in-out`}
              />
            </button>
          ))}
        </div>

        {/* Description row - only shown when a rating is selected */}
        {rating !== null && (
          <div className="text-center px-4 py-2 bg-gray-100 rounded-lg max-w-md">
            <p className="text-sm">{ratingDescriptions[rating]}</p>
          </div>
        )}
      </div>
    );
  };

  const handleSubmitRating = async () => {
    if (rating === null) {
      setError("Please select a rating before continuing.");
      return;
    }

    try {
      const currentSolution = solutions[currentSolutionIndex];
      const { data, error } = await supabase.from("evaluations").upsert(
        [
          {
            solution_id: currentSolution.id,
            evaluator_id: user.id,
            score: rating,
          },
        ],
        { onConflict: ["solution_id", "evaluator_id"] }
      );

      if (error) throw error;

      // Create updated evaluations object
      const updatedEvaluations = {
        ...userEvaluations,
        [currentSolution.id]: rating,
      };

      // Update state
      setUserEvaluations(updatedEvaluations);

      // Check if this was the last evaluation needed
      const allSolutionsEvaluated = solutions.every(
        (solution) =>
          solution.id === currentSolution.id ||
          updatedEvaluations[solution.id] !== undefined
      );

      if (allSolutionsEvaluated) {
        setShowSummary(true);
      } else {
        // Pass the updated evaluations object to ensure we have the latest data
        moveToNextUnevaluatedSolution(updatedEvaluations);
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
      setError("Failed to submit rating. Please try again.");
    }
  };

  const moveToNextUnevaluatedSolution = (evaluations = userEvaluations) => {
    // Check if all solutions have been evaluated
    const allEvaluated = solutions.every(
      (solution) => evaluations[solution.id] !== undefined
    );

    if (allEvaluated) {
      // If all solutions are evaluated, show the summary
      setShowSummary(true);
      return;
    }

    // Find next unevaluated solution
    let nextIndex = -1;

    // First, try to find an unevaluated solution after the current index
    for (let i = currentSolutionIndex + 1; i < solutions.length; i++) {
      if (evaluations[solutions[i].id] === undefined) {
        nextIndex = i;
        break;
      }
    }

    // If none found after current index, look from the beginning
    if (nextIndex === -1) {
      for (let i = 0; i < currentSolutionIndex; i++) {
        if (evaluations[solutions[i].id] === undefined) {
          nextIndex = i;
          break;
        }
      }
    }

    if (nextIndex === -1) {
      // If we still haven't found any unevaluated solutions,
      // double-check all solutions
      const anyUnevaluated = solutions.findIndex(
        (s) => evaluations[s.id] === undefined
      );

      if (anyUnevaluated !== -1) {
        setCurrentSolutionIndex(anyUnevaluated);
      } else {
        // No unevaluated solutions found, show summary
        setShowSummary(true);
      }
    } else {
      setCurrentSolutionIndex(nextIndex);
    }

    setRating(null);
  };

  const renderHTML = (content) => {
    return { __html: content };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-md mx-auto mt-8">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!userSolution) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Nemáte riešenie
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="mb-6">
            Nemôžete hodnotiť riešenia, kým nepredložíte vlastné riešenie tejto
            situácie.
          </p>
          <Button onClick={() => navigate(`/situation/${id}`)}>
            Riešiť situáciu
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (showSummary) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Súhrn vašich hodnotení
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-2">
              Problematická situácia:
            </h3>
            <div dangerouslySetInnerHTML={renderHTML(situation.description)} />
          </div>
          <Separator />
          <div>
            <h3 className="font-semibold text-lg mb-2">Vaše riešenie:</h3>
            <div dangerouslySetInnerHTML={renderHTML(userSolution.content)} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:space-x-2 space-y-1 sm:space-y-0 mt-2">
              <span className="font-medium text-sm sm:text-base">
                Vaše sebahodnotenie:
              </span>
              <div className="flex">
                {userEvaluations[userSolution.id] === 0 ? (
                  <XCircle className={`w-5 h-5 ${ratingColors[0]}`} />
                ) : (
                  [1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= userEvaluations[userSolution.id]
                          ? ratingColors[userEvaluations[userSolution.id]]
                          : "text-gray-300"
                      }`}
                    />
                  ))
                )}
              </div>
              <span className="text-xs sm:text-sm text-gray-600 truncate max-w-[180px] sm:max-w-none">
                ({ratingDescriptions[userEvaluations[userSolution.id]]})
              </span>
            </div>
          </div>
          <Separator />
          <div>
            <h3 className="font-semibold text-lg mb-2">Vaše hodnotenia:</h3>
            {solutions
              .filter((s) => !s.isUserSolution)
              .map((solution, index) => (
                <Card key={solution.id} className="mb-4">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <span>Riešenie {index + 1}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      dangerouslySetInnerHTML={renderHTML(solution.content)}
                    />
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="font-medium">Vaše hodnotenie:</span>
                      <div className="flex">
                        {userEvaluations[solution.id] === 0 ? (
                          <XCircle className={`w-5 h-5 ${ratingColors[0]}`} />
                        ) : (
                          [1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-5 h-5 ${
                                star <= userEvaluations[solution.id]
                                  ? ratingColors[userEvaluations[solution.id]]
                                  : "text-gray-300"
                              }`}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={() => navigate("/situations")} className="w-full">
            Späť na zoznam situácií
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const currentSolution = solutions[currentSolutionIndex];
  const progress =
    ((Object.keys(userEvaluations).length -
      (userEvaluations[userSolution.id] ? 1 : 0)) /
      (solutions.length - 1)) *
    100;

  const hasVoted = userEvaluations[currentSolution.id] !== undefined;

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="space-y-1">
        <div className="flex justify-between items-center">
          <CardTitle className="text-2xl font-bold">
            Hodnotenie riešení
          </CardTitle>

          {currentSolution.isUserSolution && (
            <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">Vaše Riešenie</span>
            </div>
          )}
        </div>
        <CardDescription>
          {currentSolution.isUserSolution
            ? "Ohodnoťte svoje vlastné riešenie. Môžete zmeniť svoj názor po prezretí iných riešení."
            : "Pozorne si prečítajte riešenie situácie a ohodnoťte ho pomocou hviezdičiek."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Progress value={progress} className="w-full" />

        <div>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg mb-2">
              Problematická situácia:
            </h3>
            <TextToSpeech text={situation?.description || ""} />
          </div>
          <div dangerouslySetInnerHTML={renderHTML(situation?.description)} />
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg flex items-center">
              Riešenie č.{currentSolutionIndex + 1}
              {currentSolution.isUserSolution && (
                <span className="ml-2 text-sm font-normal text-green-600">
                  (Vaše riešenie)
                </span>
              )}
            </h3>
            <TextToSpeech text={currentSolution?.content || ""} />
          </div>
          <Card>
            <CardContent className="p-4 bg-gray-50">
              <div
                dangerouslySetInnerHTML={renderHTML(currentSolution?.content)}
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Hodnotenie</h3>
          <div className="flex justify-center">
            <TooltipProvider delayDuration={0}>
              {renderRatingStars()}
            </TooltipProvider>
          </div>
          <p className="text-center text-sm text-gray-600">
            {hasVoted ? (
              <>
                Vaše hodnotenie:{" "}
                <span className="font-medium">
                  {ratingDescriptions[userEvaluations[currentSolution.id]]}
                </span>
              </>
            ) : rating === null ? (
              "Vyberte hodnotenie"
            ) : (
              <>
                Vaše hodnotenie:{" "}
                <span className="font-medium">
                  {ratingDescriptions[rating]}
                </span>
              </>
            )}
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={() => {
            const prevIndex = solutions.findIndex(
              (s, index) =>
                index < currentSolutionIndex && !userEvaluations[s.id]
            );
            if (prevIndex !== -1) {
              setCurrentSolutionIndex(prevIndex);
              setRating(0);
            } else {
              // If no unevaluated previous solution, go to the last evaluated solution
              const lastEvaluatedIndex = solutions
                .slice(0, currentSolutionIndex)
                .map((s, index) => ({
                  index,
                  evaluated: !!userEvaluations[s.id],
                }))
                .filter((s) => s.evaluated)
                .pop()?.index;
              if (lastEvaluatedIndex !== undefined) {
                setCurrentSolutionIndex(lastEvaluatedIndex);
                setRating(userEvaluations[solutions[lastEvaluatedIndex].id]);
              }
            }
          }}
          disabled={currentSolutionIndex === 0}
        >
          <ChevronLeft className="mr-2 h-4 w-4" /> Predchádzajúce
        </Button>
        {userEvaluations[solutions[currentSolutionIndex].id] !== undefined ? (
          <Button
            onClick={moveToNextUnevaluatedSolution}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {currentSolutionIndex === solutions.length - 1
              ? "Dokončiť"
              : "Ďalšie"}
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmitRating}
            disabled={rating === null}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {rating === null ? "Vyberte hodnotenie" : "Hodnotiť"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default EvaluateSolutions;
